/*
 * Copyright 2026 Sony Corporation
 */

import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import type { Scene } from '@babylonjs/core/scene';

/** Drawn after everything else with the depth cleared, so a point behind a splat's haze stays visible. */
const OVERLAY_GROUP = 1;
const COLOR = new Color3(1, 0.8, 0.1);

export interface MeasureMarkers {
  /** Dots at the points and, for two, the line between them. `size` is a dot's diameter in scene units. */
  show: (points: readonly Vector3[], size: number) => void;
  dispose: () => void;
}

/** What the known-distance tool draws while it is being used. Never part of the document, so never saved. */
export const createMeasureMarkers = (scene: Scene): MeasureMarkers => {
  const material = new StandardMaterial('measure:material', scene);
  material.emissiveColor = COLOR;
  material.disableLighting = true;
  let parts: (Mesh | LinesMesh)[] = [];

  const clear = (): void => {
    for (const part of parts) {
      part.dispose();
    }
    parts = [];
  };

  return {
    show: (points, size) => {
      clear();
      for (const point of points) {
        const dot = MeshBuilder.CreateSphere('measure:point', { diameter: size, segments: 8 }, scene);
        dot.position = point.clone();
        dot.material = material;
        parts.push(dot);
      }
      if (points.length === 2) {
        const line = MeshBuilder.CreateLines('measure:line', { points: [points[0], points[1]] }, scene);
        line.color = COLOR;
        parts.push(line);
      }
      for (const part of parts) {
        part.isPickable = false;
        part.renderingGroupId = OVERLAY_GROUP;
      }
    },
    dispose: () => {
      clear();
      material.dispose();
    },
  };
};
